// Roles & permissions — the server-role matrix and how it interacts with
// vault roles. Sourced from specs/cli.md §3 (normative table).

import { IconCheck, IconShieldLock } from "@tabler/icons-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DocSection, K, P, Points } from "../doc-ui";

function Yes() {
  return <IconCheck className="size-4 text-emerald-500" aria-label="yes" />;
}

export function RolesDoc() {
  return (
    <>
      <P>
        Two independent layers control what someone can do: a <strong>server role</strong> (one
        per account, about the instance) and a <strong>vault role</strong> (one per vault
        membership, about content). Owning a vault doesn't make you a server admin, and being a
        server admin doesn't silently put you inside anyone's vault.
      </P>

      <DocSection icon={IconShieldLock} title="Server roles">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Role</TableHead>
                <TableHead>Operate instance</TableHead>
                <TableHead>Onboard users</TableHead>
                <TableHead>Create vaults</TableHead>
                <TableHead>See user directory</TableHead>
                <TableHead>Max vault role</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell className="font-mono">admin</TableCell>
                <TableCell><Yes /></TableCell>
                <TableCell><Yes /></TableCell>
                <TableCell><Yes /></TableCell>
                <TableCell><Yes /></TableCell>
                <TableCell className="font-mono">owner</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-mono">manager</TableCell>
                <TableCell />
                <TableCell><Yes /></TableCell>
                <TableCell><Yes /></TableCell>
                <TableCell><Yes /></TableCell>
                <TableCell className="font-mono">owner</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-mono">member</TableCell>
                <TableCell />
                <TableCell />
                <TableCell><Yes /></TableCell>
                <TableCell><Yes /></TableCell>
                <TableCell className="font-mono">owner</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-mono">guest</TableCell>
                <TableCell />
                <TableCell />
                <TableCell />
                <TableCell />
                <TableCell className="font-mono">viewer</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
        <Points>
          <li>
            <strong>Operate instance</strong> — server maintenance, quotas, user admin
            (disable, reset, roles).
          </li>
          <li>
            <strong>Onboard users</strong> — mint account invites.
          </li>
          <li>
            <K>member</K> is the default for new accounts. <K>guest</K> is for read-only
            outsiders: no directory access, no vault creation, capped at viewer everywhere.
          </li>
        </Points>
      </DocSection>

      <DocSection icon={IconShieldLock} title="Vault roles">
        <P>
          Within each vault: <K>owner</K> (manage members + delete), <K>editor</K> (write +
          restore history), <K>viewer</K> (read-only). Details and guardrails are in{" "}
          <em>Share a vault &amp; manage members</em>.
        </P>
      </DocSection>

      <DocSection icon={IconShieldLock} title="Changing roles">
        <Points>
          <li>
            Server roles: admins change them on the <K>Users</K> page or with{" "}
            <K>cairn user set-role &lt;user&gt; &lt;role&gt;</K>. The last admin can't be demoted.
          </li>
          <li>Vault roles: any owner of that vault, in its Members panel or via the CLI.</li>
        </Points>
      </DocSection>
    </>
  );
}
